package com.eladfinish.bible.contest.screens.pairMatching

import androidx.compose.runtime.Stable
import com.eladfinish.bible.contest.arch.ActionKt
import com.eladfinish.bible.contest.arch.EffectKt
import com.eladfinish.bible.contest.arch.StateKt
import com.eladfinish.bible.contest.screens.pairMatching.model.GamePhase
import com.eladfinish.bible.contest.screens.pairMatching.model.PairsTopic
import dev.icerock.moko.resources.desc.StringDesc
import kotlinx.collections.immutable.ImmutableList

data class PairMatchingState(
    val dialogState: DialogState,
    val gamePhase: GamePhase = GamePhase.Idle,
    val isLoading: Boolean = false,
) : StateKt {

    @Stable
    sealed class DialogState {
        data class TableOfContents(val items: ImmutableList<PairsTopic>) : DialogState()
        data object CopyPairs : DialogState()
        data object None : DialogState()
    }
}

sealed class PairMatchingAction : ActionKt {
    // ── Dialog ────────────────────────────────────────────────────────────────
    data object HideDialog : PairMatchingAction()
    data object ShowCopyPairsDialog : PairMatchingAction()

    /** Copies pairs to clipboard — [copyAll] false = only unmatched pairs. */
    data class RequestCopy(val copyAll: Boolean) : PairMatchingAction()
    data class UpdateTopics(val data: String) : PairMatchingAction()

    // ── Game lifecycle ────────────────────────────────────────────────────────
    /** User tapped a topic row (index ≥ 2 = real topic, 0/1 are special). */
    data class SelectTopic(val index: Int) : PairMatchingAction()
    data object NewGame : PairMatchingAction()
    data object ShowSolution : PairMatchingAction()
    data object Refresh : PairMatchingAction()

    // ── Drag-and-drop result ──────────────────────────────────────────────────
    /** User dropped [valueId] onto [keyId]. */
    data class AttemptMatch(val keyId: Int, val valueId: Int) : PairMatchingAction()

    // ── Loading ───────────────────────────────────────────────────────────────
    /** Triggered by the screen on first composition. */
    data object LoadTopics : PairMatchingAction()

    /** Triggered when the user finishes sorting all items correctly in Sortable mode. */
    data object SortableCompleted : PairMatchingAction()
    data class SetLoading(val isLoading: Boolean) : PairMatchingAction()
    data class HandleLoadError(val isNetworkError: Boolean) : PairMatchingAction()
}

sealed class PairMatchingEffect : EffectKt {
    data class ShowToast(val message: String) : PairMatchingEffect()
    data class ShowToastDesc(val message: StringDesc) : PairMatchingEffect()
    data object PlaySuccessSound : PairMatchingEffect()

    /** Vibrate for a correct match. */
    data class VibrateCorrect(val millis: Long) : PairMatchingEffect()

    /** Short vibrate for a wrong match. */
    data class VibrateWrong(val millis: Long) : PairMatchingEffect()

    /** Navigate to the MultipleChoiceQuestions screen. */
    data object NavigateToMultipleChoice : PairMatchingEffect()

    /** Copy [text] to the system clipboard and show a confirmation toast. */
    data class CopyToClipboard(val text: String) : PairMatchingEffect()
}
